import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Search,
} from "lucide-react";
import { useUsage } from "../hooks/use-usage";
import { TokenUsageHistoryModel } from "@/api/models/api-types";
import { useDebounce } from "@/hooks/use-debounce";
import { getPaginationItems, PAGINATION_DOTS } from "@/lib/pagination";
import type { PaginationItem } from "@/lib/pagination";
import { isMobile as detectMobile } from "@/lib/is-mobile";

interface UsageRecord {
  modelEndpoint: string;
  requestCount: number;
  unitPrice: number;
  totalTokens: number;
}

// Search input component - outside main component to prevent re-renders
const SearchInput: React.FC<{
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onPageReset: () => void;
  placeholder: string;
}> = React.memo(({ searchTerm, onSearchChange, onPageReset, placeholder }) => (
  <div className="relative">
    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-quaternary dark:text-gray-400" />
    <input
      type="text"
      placeholder={placeholder}
      value={searchTerm}
      onChange={(e) => {
        onSearchChange(e.target.value);
        onPageReset();
      }}
      className="w-full sm:w-64 pl-10 pr-4 py-2 border border-border-secondary dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-text-primary dark:text-gray-100 placeholder-text-quaternary dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
    />
  </div>
));

export const UsageHistorySection: React.FC = () => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState<keyof UsageRecord | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [searchTerm, setSearchTerm] = useState("");
  const [isMobile, setIsMobile] = useState(() => detectMobile());

  // Debounced search term for API calls
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Map UI field names to backend field names
  const getBackendFieldName = (uiField: keyof UsageRecord): string => {
    const fieldMapping: Record<keyof UsageRecord, string> = {
      modelEndpoint: "modelTitle",
      requestCount: "usageCount",
      unitPrice: "unitPrice",
      totalTokens: "totalTokens",
    };
    return fieldMapping[uiField];
  };

  // Memoize API parameters for query
  const apiParams = useMemo(
    () => ({
      page: currentPage,
      sortBy: sortField
        ? sortDirection === "desc"
          ? `-${getBackendFieldName(sortField)}`
          : getBackendFieldName(sortField)
        : undefined,
      search: debouncedSearchTerm || undefined,
    }),
    [currentPage, sortField, sortDirection, debouncedSearchTerm]
  );

  // API hook - handles all data and state
  const { usageHistory, totalPages, isUsageHistoryLoading, usageHistoryError } =
    useUsage(apiParams);

  useEffect(() => {
    const updateIsMobile = () => {
      setIsMobile(detectMobile());
    };

    updateIsMobile();
    window.addEventListener("resize", updateIsMobile);
    return () => window.removeEventListener("resize", updateIsMobile);
  }, []);

  const paginationItems = useMemo(
    () =>
      getPaginationItems({
        totalPages,
        currentPage,
        maxLength: isMobile ? 5 : 9,
      }),
    [currentPage, isMobile, totalPages]
  );

  // Transform API data to match UI interface
  const transformedUsageHistory: UsageRecord[] = usageHistory.map(
    (item: TokenUsageHistoryModel) => ({
      modelEndpoint: item.modelTitle,
      requestCount: item.usageCount,
      unitPrice: item.totalTokens / item.usageCount,
      totalTokens: item.totalTokens,
    })
  );

  const handleSort = (field: keyof UsageRecord) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
    setCurrentPage(1);
  };

  const SortIcon = ({ field }: { field: keyof UsageRecord }) => {
    if (sortField !== field) return null;
    return sortDirection === "asc" ? (
      <ChevronUp className="w-3 h-3" />
    ) : (
      <ChevronDown className="w-3 h-3" />
    );
  };

  // Loading state
  if (isUsageHistoryLoading) {
    return (
      <div className="rounded-xl dark:border-gray-600 bg-bg-primary dark:bg-gray-800 p-4 flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <p className="text-lg font-semibold text-text-primary dark:text-gray-100">
            {t("settings.usage.usageHistoryByModel")}
          </p>
          <SearchInput
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            onPageReset={() => setCurrentPage(1)}
            placeholder={t("common.search")}
          />
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="text-sm text-text-secondary dark:text-gray-400">
            {t("common.loading")}...
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (usageHistoryError) {
    return (
      <div className="rounded-xl dark:border-gray-600 bg-bg-primary dark:bg-gray-800 p-4 flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <p className="text-lg font-semibold text-text-primary dark:text-gray-100">
            {t("settings.usage.usageHistoryByModel")}
          </p>
          <SearchInput
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            onPageReset={() => setCurrentPage(1)}
            placeholder={t("common.search")}
          />
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="text-sm text-red-500">
            {t("common.error")}: {usageHistoryError.message}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl dark:border-gray-600 bg-bg-primary dark:bg-gray-800 p-4 flex flex-col gap-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <p className="text-lg font-semibold text-text-primary dark:text-gray-100">
          {t("settings.usage.usageHistoryByModel")}
        </p>
        <SearchInput
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onPageReset={() => setCurrentPage(1)}
          placeholder={t("common.search")}
        />
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            {/* Table Header */}
            <thead className="bg-white dark:bg-gray-800 border border-border-secondary dark:border-border-secondary rounded-xl overflow-hidden">
              <tr>
                <th className="px-6 py-3 text-left">
                  <div className="flex items-center gap-1">
                    <span className="text-xs font-semibold text-text-quaternary dark:text-gray-400">
                      {t("settings.usage.modelEndpoint")}
                    </span>
                  </div>
                </th>
                <th className="px-6 py-3 text-left w-[200px]">
                  <button
                    onClick={() => handleSort("requestCount")}
                    className="flex items-center gap-1 hover:text-text-secondary dark:hover:text-gray-300 transition-colors"
                  >
                    <span className="text-xs font-semibold text-text-quaternary dark:text-gray-400">
                      {t("settings.usage.requestCount")}
                    </span>
                    <SortIcon field="requestCount" />
                  </button>
                </th>
                <th className="px-6 py-3 text-left w-[200px]">
                  <button
                    onClick={() => handleSort("unitPrice")}
                    className="flex items-center gap-1 hover:text-text-secondary dark:hover:text-gray-300 transition-colors"
                  >
                    <span className="text-xs font-semibold text-text-quaternary dark:text-gray-400">
                      {t("settings.usage.unitPrice")}
                    </span>
                    <SortIcon field="unitPrice" />
                  </button>
                </th>
                <th className="px-6 py-3 text-left w-[200px]">
                  <button
                    onClick={() => handleSort("totalTokens")}
                    className="flex items-center gap-1 hover:text-text-secondary dark:hover:text-gray-300 transition-colors"
                  >
                    <span className="text-xs font-semibold text-text-quaternary dark:text-gray-400">
                      {t("settings.usage.cost")}
                    </span>
                    <SortIcon field="totalTokens" />
                  </button>
                </th>
              </tr>
            </thead>

            {/* Table Body */}
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-border-secondary dark:divide-gray-600">
              {transformedUsageHistory.map((record, index) => (
                <tr
                  key={index}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <td className="px-6 py-4">
                    <span className="text-sm text-text-tertiary dark:text-gray-300">
                      {record.modelEndpoint}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm font-medium text-text-primary dark:text-gray-100">
                      {record.requestCount}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm font-medium text-text-primary dark:text-gray-100">
                      {record.unitPrice.toFixed(3)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm font-medium text-text-primary dark:text-gray-100">
                      {record.totalTokens}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Desktop Pagination */}
        <div className="border-t border-border-secondary dark:border-gray-600 px-6 py-3 flex items-center justify-between">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="flex items-center gap-1 px-3.5 py-2 border border-border-secondary dark:border-gray-600 rounded-lg text-sm font-semibold text-text-secondary dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label={t("settings.usage.previous")}
          >
            <ChevronLeft className="w-4 h-4" />
            {!isMobile && <span>{t("settings.usage.previous")}</span>}
          </button>

          <div className="flex gap-0.5">
            {paginationItems.map((item: PaginationItem, index) => {
              if (item === PAGINATION_DOTS) {
                return (
                  <span
                    key={`desktop-usage-dots-${index}`}
                    className="px-2 text-text-quaternary dark:text-gray-400"
                  >
                    ...
                  </span>
                );
              }

              const page = item as number;

              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-medium transition-colors ${
                    currentPage === page
                      ? "bg-gray-50 dark:bg-gray-700 text-text-secondary dark:text-gray-200"
                      : "text-text-quaternary dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                  }`}
                >
                  {page}
                </button>
              );
            })}
          </div>

          <button
            onClick={() =>
              setCurrentPage(Math.min(totalPages, currentPage + 1))
            }
            disabled={currentPage === totalPages}
            className="flex items-center gap-1 px-3.5 py-2 border border-border-secondary dark:border-gray-600 rounded-lg text-sm font-semibold text-text-secondary dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label={t("settings.usage.next")}
          >
            {!isMobile && <span>{t("settings.usage.next")}</span>}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden flex flex-col gap-3">
        {transformedUsageHistory.map((record, index) => (
          <div
            key={index}
            className="bg-white dark:bg-gray-800 border border-border-secondary dark:border-gray-600 rounded-xl p-3"
          >
            <div className="flex flex-col gap-2">
              {/* Model Endpoint */}
              <div>
                <p className="text-sm font-medium text-text-tertiary dark:text-gray-300 truncate">
                  {record.modelEndpoint}
                </p>
              </div>

              {/* Grid: 2 rows x 2 cols - Compact Layout */}
              <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                {/* Request Count */}
                <div>
                  <p className="text-[10px] text-text-quaternary dark:text-gray-400 mb-0.5">
                    {t("settings.usage.requestCount")}
                  </p>
                  <p className="text-xs font-medium text-text-primary dark:text-gray-100">
                    {record.requestCount}
                  </p>
                </div>

                {/* Unit Price */}
                <div>
                  <p className="text-[10px] text-text-quaternary dark:text-gray-400 mb-0.5">
                    {t("settings.usage.unitPrice")}
                  </p>
                  <p className="text-xs font-medium text-text-primary dark:text-gray-100">
                    {record.unitPrice.toFixed(3)}
                  </p>
              </div>

              {/* Cost */}
                <div className="col-span-2">
                  <p className="text-[10px] text-text-quaternary dark:text-gray-400 mb-0.5">
                  {t("settings.usage.cost")}
                </p>
                  <p className="text-xs font-medium text-text-primary dark:text-gray-100">
                  {record.totalTokens}
                </p>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Mobile Pagination */}
        <div className="flex items-center justify-center gap-2 pt-4">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="flex items-center gap-1 px-3 py-2 border border-border-secondary dark:border-gray-600 rounded-lg text-xs font-semibold text-text-secondary dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label={t("settings.usage.previous")}
          >
            <ChevronLeft className="w-3 h-3" />
            {!isMobile && <span>{t("settings.usage.previous")}</span>}
          </button>

          <div className="flex gap-1">
            {paginationItems.map((item: PaginationItem, index) => {
              if (item === PAGINATION_DOTS) {
                return (
                  <span
                    key={`mobile-usage-dots-${index}`}
                    className="px-2 text-text-quaternary dark:text-gray-400"
                  >
                    ...
                  </span>
                );
              }

              const page = item as number;

              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-medium transition-colors ${
                    currentPage === page
                      ? "bg-gray-50 dark:bg-gray-700 text-text-secondary dark:text-gray-200"
                      : "text-text-quaternary dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                  }`}
                >
                  {page}
                </button>
              );
            })}
          </div>

          <button
            onClick={() =>
              setCurrentPage(Math.min(totalPages, currentPage + 1))
            }
            disabled={currentPage === totalPages}
            className="flex items-center gap-1 px-3 py-2 border border-border-secondary dark:border-gray-600 rounded-lg text-xs font-semibold text-text-secondary dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label={t("settings.usage.next")}
          >
            {!isMobile && <span>{t("settings.usage.next")}</span>}
            <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
};
